
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/SupabaseAuthContext';
import Dashboard from '@/pages/Dashboard/DashboardPage';
import Profile from '@/pages/Profile';
import PayoutReportPage from '@/pages/PayoutReport/PayoutReportPage';
import SupportTicketPage from '@/components/support/SupportTicketPage';
import ReferralLinkTrackingPage from '@/pages/ReferralTracking/ReferralLinkTrackingPage';
import PointRedemptionPage from '@/pages/PointRedemption/PointRedemptionPage';
import NotFound from '@/pages/NotFound';
import KnowledgeBasePage from '@/pages/KnowledgeBase/KnowledgeBasePage';
import UserSettingsPage from '@/pages/UserSettings/UserSettingsPage';

interface UserRoutesProps {
  isLoggedIn?: boolean;
}

const UserRoutes: React.FC<UserRoutesProps> = ({ isLoggedIn }) => {
  // Use context if prop is not provided
  const authContext = useAuth();
  const isAuthenticated = isLoggedIn !== undefined ? isLoggedIn : authContext.isAuthenticated;
  const { isLoading, role } = authContext;
  
  console.log('UserRoutes rendering with:', { 
    isAuthenticated, 
    role, 
    propIsLoggedIn: isLoggedIn,
    contextIsAuthenticated: authContext.isAuthenticated,
    contextRole: authContext.role
  });
  
  // If still loading auth state, show loading indicator
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('UserRoutes: User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // Allow any authenticated user to access these routes, but make redirection handling better
  if (role && role !== 'user') {
    console.log(`UserRoutes: User has role ${role}, redirecting to appropriate dashboard`);
    
    // Redirect to appropriate dashboard based on role
    if (role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (role === 'superadmin') {
      return <Navigate to="/super-admin/dashboard" replace />;
    } else if (role === 'business' || role === 'manager') {
      return <Navigate to="/business/dashboard" replace />;
    } else if (role === 'masterAdmin') {
      return <Navigate to="/master/dashboard" replace />;
    }
  }
  
  return (
    <Routes>
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="profile" element={<Profile />} />
      <Route path="payout-report" element={<PayoutReportPage />} />
      <Route path="referral-tracking" element={<ReferralLinkTrackingPage />} />
      <Route path="points-redemption" element={<PointRedemptionPage />} />
      <Route path="support" element={<SupportTicketPage />} />
      <Route path="knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="settings" element={<UserSettingsPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default UserRoutes;
